import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Images } from "lucide-react";
import { api } from "../lib/api";
import { FileThumb } from "./FileThumb";
import AttachmentViewer from "./AttachmentViewer";
import HoverPreview from "./HoverPreview";

export function useGallery(dossierId, reloadKey) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(`/dossiers/${dossierId}/gallery`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])), [dossierId]);
  useEffect(() => { load(); }, [load, reloadKey]);
  return [items, load];
}

export default function AttachmentStrip({ dossierId, reloadKey }) {
  const [items] = useGallery(dossierId, reloadKey);
  const [openKey, setOpenKey] = useState(null);
  const ref = useRef(null);
  if (!items) return null;
  const scroll = (d) => ref.current?.scrollBy({ left: d * 480, behavior: "smooth" });
  return (
    <div className="card-flat p-4" data-testid="attachment-strip">
      <div className="flex items-center justify-between mb-3">
        <div className="overline flex items-center gap-1.5"><Images className="w-3 h-3" /> Pièces jointes <span className="text-norea-textMuted normal-case tracking-normal">({items.length})</span></div>
        {items.length > 0 && (
          <div className="flex items-center gap-1">
            <button onClick={() => scroll(-1)} className="p-1 rounded hover:bg-norea-surfaceHover" data-testid="attachment-strip-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => scroll(1)} className="p-1 rounded hover:bg-norea-surfaceHover" data-testid="attachment-strip-next"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={() => setOpenKey(items[0].key)} className="ml-2 text-xs text-norea-primary hover:underline" data-testid="attachment-strip-open-all">Tout parcourir</button>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-norea-textMuted italic py-3 text-center" data-testid="attachment-strip-empty">Aucune pièce jointe pour ce dossier</div>
      ) : (
        <div ref={ref} className="flex gap-3 overflow-x-auto pb-1 snap-x" data-testid="attachment-strip-list">
          {items.map((f, i) => (
            <HoverPreview key={f.key} dossierId={dossierId} file={f} className="flex-shrink-0 snap-start" testid={`attachment-strip-preview-${i}`}>
            <button onClick={() => setOpenKey(f.key)} title={f.filename}
              className="group w-28 text-left" data-testid={`attachment-strip-item-${i}`}>
              <div className="h-36 rounded-md border border-norea-border overflow-hidden transition-[transform,box-shadow] group-hover:-translate-y-0.5 group-hover:shadow-md">
                <FileThumb dossierId={dossierId} file={f} />
              </div>
              <div className="mt-1 text-[11px] text-norea-text truncate">{f.label || f.filename}</div>
              <div className="text-[10px] text-norea-textMuted truncate">{f.tab_label}</div>
            </button>
            </HoverPreview>
          ))}
        </div>
      )}
      {openKey && <AttachmentViewer dossierId={dossierId} items={items} startKey={openKey} onClose={() => setOpenKey(null)} />}
    </div>
  );
}
